import { memo, useEffect, useState } from 'react';
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@heroui/react';
import {
  useGetCertificateSignatureQuery,
  useUpsertCertificateSignatureMutation,
} from '@/entities/Certificates';
import { useToast } from '@/shared/lib/hooks/useToast';
import { scaleInOut } from '@/shared/lib/utils/modalTransitions';
import { AppButton } from '@/shared/ui/AppButton';
import { CustomFileInput } from '@/shared/ui/Fields';
import styles from './CertificateSignatureModal.module.scss';

const ACCEPTED_MIME = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const ACCEPTED_EXT = ['.jpg', '.jpeg', '.png', '.webp'];
const MAX_SIZE_BYTES = 2 * 1024 * 1024;

interface CertificateSignatureModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onSuccess?: () => void;
}

const isAcceptedFile = (file: File) => {
  const lowerName = file.name.toLowerCase();
  const hasValidExt = ACCEPTED_EXT.some((ext) => lowerName.endsWith(ext));
  const hasValidMime = !file.type || ACCEPTED_MIME.includes(file.type);
  return hasValidExt && hasValidMime;
};

export const CertificateSignatureModal = memo(
  ({ isOpen, onOpenChange, onSuccess }: CertificateSignatureModalProps) => {
    const { toast } = useToast();
    const [signatureFile, setSignatureFile] = useState<File | null>(null);
    const [fileError, setFileError] = useState<string | undefined>();

    const { data, isLoading: isSignatureLoading } = useGetCertificateSignatureQuery(undefined, {
      skip: !isOpen,
    });
    const [upsertSignature, { isLoading: isSaving }] = useUpsertCertificateSignatureMutation();

    const currentSignature = data?.data?.signature;
    const hasExistingSignature = Boolean(currentSignature);
    const modalTitle = hasExistingSignature ? 'Change signature' : 'Add signature';

    useEffect(() => {
      if (!isOpen) {
        setSignatureFile(null);
        setFileError(undefined);
      }
    }, [isOpen]);

    const handleClose = () => {
      setSignatureFile(null);
      setFileError(undefined);
      onOpenChange(false);
    };

    const handleFileChange = (file: File | null) => {
      setFileError(undefined);

      if (!file) {
        setSignatureFile(null);
        return;
      }

      if (!isAcceptedFile(file)) {
        setSignatureFile(null);
        setFileError('Allowed formats: jpg, jpeg, png, webp');
        return;
      }

      if (file.size > MAX_SIZE_BYTES) {
        setSignatureFile(null);
        setFileError('Maximum file size is 2MB');
        return;
      }

      setSignatureFile(file);
    };

    const handleSave = async () => {
      if (!signatureFile) {
        setFileError('Signature image is required');
        return;
      }

      try {
        const result = await upsertSignature({ signatureFile }).unwrap();
        toast({
          title: 'Success',
          description: result.message || 'Signature saved successfully',
          color: 'success',
        });
        handleClose();
        onSuccess?.();
      } catch (error: any) {
        toast({
          title: 'Error',
          description: error?.data?.message || 'Failed to save signature',
          color: 'danger',
        });
      }
    };

    return (
      <Modal
        isOpen={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleClose();
          } else {
            onOpenChange(true);
          }
        }}
        placement="center"
        motionProps={scaleInOut}
        size="2xl"
        classNames={{
          base: 'p-6 flex flex-col gap-5 max-w-[504px]',
          closeButton: styles.closeBtn,
        }}
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader className={styles.title}>{modalTitle}</ModalHeader>
              <ModalBody className="p-0 flex flex-col gap-4">
                <p className="text-sm text-grey-secondary">
                  Upload a signature image used on all certificates. Formats: jpg, jpeg, png, webp.
                  Max 2MB.
                </p>
                <div className={styles.previewWrap}>
                  {isSignatureLoading ? (
                    <span className="text-sm text-grey-secondary">Loading...</span>
                  ) : (
                    <CustomFileInput
                      value={signatureFile}
                      onChange={handleFileChange}
                      accept="image/jpeg,image/jpg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                      initialPreviewPicture={currentSignature?.url || null}
                      errorMessage={fileError}
                      showError={!!fileError}
                    />
                  )}
                </div>
              </ModalBody>
              <ModalFooter className="gap-4 pt-6 px-0">
                <AppButton fullWidth color="white" onPress={handleClose} type="button">
                  Cancel
                </AppButton>
                <AppButton
                  fullWidth
                  color="black"
                  type="button"
                  isLoading={isSaving}
                  disabled={!signatureFile || isSaving}
                  onPress={handleSave}
                >
                  {hasExistingSignature ? 'Save changes' : 'Add signature'}
                </AppButton>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    );
  },
);

CertificateSignatureModal.displayName = 'CertificateSignatureModal';
